import { SITE_NAME } from "@/lib/constants/site"
import { SITE_URL } from "@/lib/constants/site-url"
import { BAKERY_SCHEMA_ID } from "@/lib/data/bakery-schema"
import type { LandingPage } from "@/lib/data/landing/types"
import { jsonLd } from "@/lib/utils/json-ld"

/**
 * Title, meta and JSON-LD for a search landing page. The business itself is
 * referenced by @id rather than re-declared: the full Bakery node lives on
 * `/`, /visit and /contact, and a second copy here would read as another
 * business to Google.
 */
export function buildLandingHead(page: LandingPage) {
  const canonical = `${SITE_URL}${page.path}`
  const ogImage = `${SITE_URL}${page.hero.src}`

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${canonical}#webpage`,
    url: canonical,
    name: page.title,
    description: page.description,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": BAKERY_SCHEMA_ID },
    primaryImageOfPage: ogImage,
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: page.breadcrumb, item: canonical },
    ],
  }

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faq.map((entry) => ({
      "@type": "Question",
      name: entry.q,
      acceptedAnswer: { "@type": "Answer", text: entry.a },
    })),
  }

  return {
    meta: [
      { title: page.title },
      { name: "description", content: page.description },
      { property: "og:title", content: page.title },
      { property: "og:description", content: page.description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical },
      { property: "og:image", content: ogImage },
      { property: "og:site_name", content: SITE_NAME },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: page.title },
      { name: "twitter:description", content: page.description },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      { type: "application/ld+json", children: jsonLd(webPageSchema) },
      { type: "application/ld+json", children: jsonLd(breadcrumbSchema) },
      { type: "application/ld+json", children: jsonLd(faqSchema) },
    ],
  }
}
